/**
 * Checkout (C) state machine shared by the onboarding chooser (A) and the
 * paywall (B) hosts, on every platform.
 *
 *   picker -> review -> purchasing -> purchased | pending | cancelled | error
 *                                  -> redirecting (web checkout left the page)
 *   review -> restoring -> purchased | unchanged | error
 *   pending -> checking -> purchased | pending
 *
 * Rules:
 * - A tap is never success. `purchased` requires a synced snapshot that meets
 *   the requested tier (`subscriptionStateMeetsPurchaseTier`).
 * - One `startPurchase` per review-screen tap. Duplicate taps while a request
 *   is in flight are ignored, and `pending` can only "Check again" (sync) -
 *   it never starts another purchase, so retry can never charge twice.
 * - The existing RevenueCat provider owns purchase/restore/sync; this module
 *   only sequences them.
 */
import type {
  PaidPlanTier,
  PlanSet,
  SubscriptionStateResult,
  SubscriptionSyncInput,
  SubscriptionWebCheckoutInput,
} from '@tabbio/types';
import { subscriptionStateMeetsPurchaseTier } from '../subscription-purchase';
import type { BillingCycle } from './pricing';

export type CheckoutOrigin = 'onboarding' | 'upgrade';

export type CheckoutStatus =
  | 'picker'
  | 'review'
  | 'purchasing'
  | 'restoring'
  | 'checking'
  | 'redirecting'
  | 'purchased'
  | 'pending'
  | 'cancelled'
  | 'error'
  | 'unchanged';

export interface CheckoutSelection {
  tier: PaidPlanTier;
  cycle: BillingCycle;
}

/**
 * Why a purchase or restore ended in `error`, as the review screen explains
 * it. One short category per store/network/account situation; `unknown` keeps
 * the generic "Something didn't load" copy.
 */
export type CheckoutFailureReason =
  | 'unavailable'
  | 'network'
  | 'store'
  | 'not_allowed'
  | 'configuration'
  | 'auth'
  | 'unknown';

export const CHECKOUT_FAILURE_REASONS: ReadonlySet<CheckoutFailureReason> = new Set<CheckoutFailureReason>([
  'unavailable',
  'network',
  'store',
  'not_allowed',
  'configuration',
  'auth',
  'unknown',
]);

export interface CheckoutState {
  status: CheckoutStatus;
  selection: CheckoutSelection | null;
  /** Increments per started purchase/restore/check; stale results are dropped. */
  attempt: number;
  /** What produced the terminal state, for analytics/copy decisions. */
  lastAction: 'purchase' | 'restore' | 'check' | null;
  /** Set with `error`; cleared by the next start event. */
  failure: CheckoutFailureReason | null;
}

export type CheckoutEvent =
  | { type: 'open_review'; selection: CheckoutSelection }
  | { type: 'back_to_picker' }
  | { type: 'purchase_started' }
  | { type: 'restore_started' }
  | { type: 'check_started' }
  | { type: 'redirected'; attempt: number }
  | { type: 'verified'; attempt: number }
  | { type: 'not_verified'; attempt: number }
  | { type: 'cancelled'; attempt: number }
  | { type: 'failed'; attempt: number; reason?: CheckoutFailureReason }
  | { type: 'reset' };

export const INITIAL_CHECKOUT_STATE: CheckoutState = {
  status: 'picker',
  selection: null,
  attempt: 0,
  lastAction: null,
  failure: null,
};

/** States in which a request is running; every start event is ignored. */
export const BUSY_CHECKOUT_STATUSES: ReadonlySet<CheckoutStatus> = new Set([
  'purchasing',
  'restoring',
  'checking',
  'redirecting',
]);

/** States that show the review screen with an actionable purchase button. */
const PURCHASABLE: ReadonlySet<CheckoutStatus> = new Set([
  'review',
  'cancelled',
  'error',
  'unchanged',
]);

export function isCheckoutBusy(state: CheckoutState) {
  return BUSY_CHECKOUT_STATUSES.has(state.status);
}

export function checkoutReducer(
  state: CheckoutState,
  event: CheckoutEvent,
): CheckoutState {
  switch (event.type) {
    case 'open_review':
      if (isCheckoutBusy(state) || state.status === 'pending') return state;
      return {
        ...state,
        status: 'review',
        selection: event.selection,
        lastAction: null,
        failure: null,
      };
    case 'back_to_picker':
      // Leaving mid-request would orphan a provider sheet; pending keeps its
      // "Check again" so the person is never pushed into paying twice.
      if (isCheckoutBusy(state) || state.status === 'pending') return state;
      return { ...state, status: 'picker', lastAction: null, failure: null };
    case 'purchase_started':
      if (!state.selection || !PURCHASABLE.has(state.status)) return state;
      return {
        ...state,
        status: 'purchasing',
        attempt: state.attempt + 1,
        lastAction: 'purchase',
        failure: null,
      };
    case 'restore_started':
      if (!state.selection || !PURCHASABLE.has(state.status)) return state;
      return {
        ...state,
        status: 'restoring',
        attempt: state.attempt + 1,
        lastAction: 'restore',
        failure: null,
      };
    case 'check_started':
      if (state.status !== 'pending') return state;
      return {
        ...state,
        status: 'checking',
        attempt: state.attempt + 1,
        lastAction: 'check',
        failure: null,
      };
    case 'redirected':
      if (event.attempt !== state.attempt || state.status !== 'purchasing') {
        return state;
      }
      return { ...state, status: 'redirecting' };
    case 'verified':
      if (event.attempt !== state.attempt || !isCheckoutBusy(state)) return state;
      return { ...state, status: 'purchased' };
    case 'not_verified':
      if (event.attempt !== state.attempt || !isCheckoutBusy(state)) return state;
      // A restore that finds nothing is "unchanged", not a false success; a
      // purchase or re-check that is not yet reflected stays "pending".
      return {
        ...state,
        status: state.status === 'restoring' ? 'unchanged' : 'pending',
      };
    case 'cancelled':
      if (event.attempt !== state.attempt || state.status !== 'purchasing') {
        return state;
      }
      return { ...state, status: 'cancelled' };
    case 'failed':
      if (event.attempt !== state.attempt || !isCheckoutBusy(state)) return state;
      // A failed re-check leaves the earlier purchase unresolved, not failed.
      if (state.status === 'checking') {
        return { ...state, status: 'pending' };
      }
      return { ...state, status: 'error', failure: event.reason ?? 'unknown' };
    case 'reset':
      return INITIAL_CHECKOUT_STATE;
  }
}

/** Mirrors the provider's classification (message/userCancelled/RC code "1"). */
export function isPurchaseCancelledError(error: unknown) {
  const record =
    error && typeof error === 'object' ? (error as Record<string, unknown>) : {};
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  return (
    record.userCancelled === true ||
    record.code === '1' ||
    record.code === 'PURCHASE_CANCELLED_ERROR' ||
    readableErrorCode(record) === 'PURCHASE_CANCELLED_ERROR' ||
    message.includes('cancel')
  );
}

/** RevenueCat's `readableErrorCode` (top level, or under `userInfo` on newer SDKs). */
function readableErrorCode(record: Record<string, unknown>) {
  const userInfo =
    record.userInfo && typeof record.userInfo === 'object'
      ? (record.userInfo as Record<string, unknown>)
      : null;
  const value = userInfo?.readableErrorCode ?? record.readableErrorCode;
  return typeof value === 'string' ? value.toUpperCase() : '';
}

/** `PURCHASES_ERROR_CODE` values are numeric strings ("10" = NETWORK_ERROR). */
const RC_NETWORK_CODES = new Set(['10', '32', '35']);
const RC_NOT_ALLOWED_CODES = new Set(['3']);
const RC_UNAVAILABLE_CODES = new Set(['5', '24']);
const RC_STORE_CODES = new Set(['2', '4', '6', '7', '8', '9', '13', '18']);
const RC_CONFIGURATION_CODES = new Set(['11', '14', '17', '23']);

const NETWORK_MESSAGE_NEEDLES = [
  'network request failed',
  'failed to fetch',
  'load failed',
  'networkerror',
  'request aborted',
  'offline',
  'timed out',
  'timeout',
];

/**
 * Maps a purchase/restore failure onto the review's error copy. RevenueCat
 * errors are read by code first (the message is localized by the store),
 * then our own thrown messages, then the network/auth shapes the sync API
 * produces. Cancellation is not a failure and is classified separately.
 */
export function classifyCheckoutFailure(error: unknown): CheckoutFailureReason {
  const record =
    error && typeof error === 'object' ? (error as Record<string, unknown>) : {};
  const explicit = record.reason;
  if (typeof explicit === 'string' && CHECKOUT_FAILURE_REASONS.has(explicit as CheckoutFailureReason)) {
    return explicit as CheckoutFailureReason;
  }

  const code = typeof record.code === 'string' ? record.code : '';
  const readable = readableErrorCode(record);
  const message = error instanceof Error ? error.message.toLowerCase() : '';

  if (
    RC_NETWORK_CODES.has(code) ||
    readable === 'NETWORK_ERROR' ||
    readable === 'OFFLINE_CONNECTION_ERROR' ||
    readable === 'PRODUCT_REQUEST_TIMED_OUT_ERROR' ||
    record.name === 'AbortError' ||
    NETWORK_MESSAGE_NEEDLES.some((needle) => message.includes(needle))
  ) {
    return 'network';
  }
  if (RC_NOT_ALLOWED_CODES.has(code) || readable === 'PURCHASE_NOT_ALLOWED_ERROR') {
    return 'not_allowed';
  }
  if (
    RC_UNAVAILABLE_CODES.has(code) ||
    readable === 'PRODUCT_NOT_AVAILABLE_FOR_PURCHASE_ERROR' ||
    readable === 'UNSUPPORTED_ERROR' ||
    code === 'SUBSCRIPTION_BILLING_UNAVAILABLE' ||
    message.includes('revenuecat offering') ||
    message.includes('no revenuecat package is available')
  ) {
    return 'unavailable';
  }
  if (
    RC_CONFIGURATION_CODES.has(code) ||
    readable === 'CONFIGURATION_ERROR' ||
    readable === 'INVALID_CREDENTIALS_ERROR' ||
    message.includes('revenuecat api key is missing') ||
    message.includes('native module is unavailable')
  ) {
    return 'configuration';
  }
  if (RC_STORE_CODES.has(code) || readable === 'STORE_PROBLEM_ERROR') {
    return 'store';
  }
  if (
    record.name === 'AppAuthRequiredError' ||
    message.includes('authentication required') ||
    message.includes('sign in to manage your subscription')
  ) {
    return 'auth';
  }
  return 'unknown';
}

/**
 * Copy keys (`common`) for the review's error state, by failure reason.
 * `configuration` reads as "unavailable" to the person; analytics keep the
 * precise reason.
 */
export function checkoutFailureCopy(reason: CheckoutFailureReason | null | undefined): {
  titleKey: string;
  bodyKey: string;
  icon: 'warning' | 'info';
} {
  switch (reason) {
    case 'unavailable':
    case 'configuration':
      return {
        titleKey: 'subscription.checkout.errors.unavailable.title',
        bodyKey: 'subscription.checkout.errors.unavailable.body',
        icon: 'info',
      };
    case 'network':
      return {
        titleKey: 'subscription.checkout.errors.network.title',
        bodyKey: 'subscription.checkout.errors.network.body',
        icon: 'warning',
      };
    case 'store':
      return {
        titleKey: 'subscription.checkout.errors.store.title',
        bodyKey: 'subscription.checkout.errors.store.body',
        icon: 'warning',
      };
    case 'not_allowed':
      return {
        titleKey: 'subscription.checkout.errors.notAllowed.title',
        bodyKey: 'subscription.checkout.errors.notAllowed.body',
        icon: 'info',
      };
    case 'auth':
      return {
        titleKey: 'subscription.checkout.errors.signIn.title',
        bodyKey: 'subscription.checkout.errors.signIn.body',
        icon: 'info',
      };
    default:
      return {
        titleKey: 'subscription.states.error.title',
        bodyKey: 'subscription.states.error.body',
        icon: 'warning',
      };
  }
}

/**
 * What the provider reports back from a purchase tap. `purchased`: the store
 * confirmed the charge (or the server already showed the tier); `pending`: the
 * store deferred it (Ask to Buy, pending payment); `checkout`: the web left
 * for hosted billing. None of them is the entitlement: only `verify()` is.
 */
export type CheckoutPurchaseOutcome = 'purchased' | 'checkout' | 'pending';

/** The subset of `useRevenueCat()` the flow drives. */
export interface CheckoutBilling {
  startPurchase: (
    input: SubscriptionWebCheckoutInput,
  ) => Promise<CheckoutPurchaseOutcome>;
  syncSubscription: (
    input?: SubscriptionSyncInput,
  ) => Promise<SubscriptionStateResult | null>;
  restorePurchases: (
    input?: SubscriptionSyncInput,
  ) => Promise<SubscriptionStateResult | null>;
}

export interface CheckoutControllerOptions {
  billing: CheckoutBilling;
  planSet: PlanSet;
  companyId?: string | null;
  /** `native` on iOS/Android, `web` for the hosted web checkout. */
  surface: SubscriptionWebCheckoutInput['surface'];
  /**
   * Fires once when a synced snapshot confirms the tier. Hosts route here:
   * onboarding -> `persistPlanAndAdvance(tier)`, paywall -> welcome screen.
   */
  onPurchased?: (selection: CheckoutSelection) => void;
}

type Listener = () => void;

/**
 * Framework-free store around the reducer. `useCheckoutFlow` binds it to React
 * with `useSyncExternalStore`; tests drive it directly.
 */
export class CheckoutController {
  private state: CheckoutState = INITIAL_CHECKOUT_STATE;
  private listeners = new Set<Listener>();
  /** Synchronous guard: two taps in one frame reach here before any re-render. */
  private inFlight = false;

  constructor(private options: CheckoutControllerOptions) {}

  setOptions(options: CheckoutControllerOptions) {
    this.options = options;
  }

  getState = () => this.state;

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  dispatch(event: CheckoutEvent) {
    const next = checkoutReducer(this.state, event);
    if (next === this.state) return;
    const becamePurchased =
      next.status === 'purchased' && this.state.status !== 'purchased';
    this.state = next;
    this.listeners.forEach((listener) => listener());
    if (becamePurchased && next.selection) {
      this.options.onPurchased?.(next.selection);
    }
  }

  openReview(selection: CheckoutSelection) {
    this.dispatch({ type: 'open_review', selection });
  }

  backToPicker() {
    this.dispatch({ type: 'back_to_picker' });
  }

  reset() {
    this.inFlight = false;
    this.dispatch({ type: 'reset' });
  }

  private syncInput(): SubscriptionSyncInput {
    return {
      planSet: this.options.planSet,
      companyId: this.options.companyId ?? null,
    };
  }

  private meetsSelection(
    result: SubscriptionStateResult | null,
    selection: CheckoutSelection,
  ) {
    return subscriptionStateMeetsPurchaseTier(result, {
      planSet: this.options.planSet,
      tier: selection.tier,
      companyId: this.options.companyId ?? null,
    });
  }

  /** "Continue to App Store / Google Play / billing". */
  async purchase() {
    const selection = this.state.selection;
    if (this.inFlight || !selection) return;
    this.dispatch({ type: 'purchase_started' });
    if (this.state.status !== 'purchasing') return;
    this.inFlight = true;
    const attempt = this.state.attempt;
    try {
      const outcome = await this.options.billing.startPurchase({
        planSet: this.options.planSet,
        tier: selection.tier,
        billingInterval: selection.cycle,
        companyId:
          this.options.planSet === 'employer'
            ? (this.options.companyId ?? null)
            : null,
        surface: this.options.surface,
      });
      if (outcome === 'checkout') {
        this.dispatch({ type: 'redirected', attempt });
        return;
      }
      // The store confirmed (`purchased`) or deferred (`pending`) the charge.
      // Only a synced snapshot proves the entitlement, and a sync that fails
      // now reads as pending + "Check again", never as a failed purchase.
      await this.verify(attempt, selection);
    } catch (error) {
      this.dispatch(
        isPurchaseCancelledError(error)
          ? { type: 'cancelled', attempt }
          : { type: 'failed', attempt, reason: classifyCheckoutFailure(error) },
      );
    } finally {
      this.inFlight = false;
    }
  }

  /** "Restore an existing purchase" / "Restore". */
  async restore() {
    const selection = this.state.selection;
    if (this.inFlight || !selection) return;
    this.dispatch({ type: 'restore_started' });
    if (this.state.status !== 'restoring') return;
    this.inFlight = true;
    const attempt = this.state.attempt;
    try {
      const result = await this.options.billing.restorePurchases(this.syncInput());
      this.dispatch({
        type: this.meetsSelection(result, selection) ? 'verified' : 'not_verified',
        attempt,
      });
    } catch (error) {
      this.dispatch({ type: 'failed', attempt, reason: classifyCheckoutFailure(error) });
    } finally {
      this.inFlight = false;
    }
  }

  /** Pending "Check again": sync only, never another purchase. */
  async checkAgain() {
    const selection = this.state.selection;
    if (this.inFlight || !selection) return;
    this.dispatch({ type: 'check_started' });
    if (this.state.status !== 'checking') return;
    this.inFlight = true;
    const attempt = this.state.attempt;
    try {
      await this.verify(attempt, selection);
    } finally {
      this.inFlight = false;
    }
  }

  private async verify(attempt: number, selection: CheckoutSelection) {
    try {
      const result = await this.options.billing.syncSubscription(this.syncInput());
      this.dispatch({
        type: this.meetsSelection(result, selection) ? 'verified' : 'not_verified',
        attempt,
      });
    } catch {
      // The charge may well have gone through; say "pending", not "failed".
      this.dispatch({ type: 'not_verified', attempt });
    }
  }
}

/** Copy keys (`common`) for the non-ready checkout states. */
export const CHECKOUT_STATE_COPY: Partial<
  Record<CheckoutStatus, { titleKey: string; bodyKey?: string }>
> = {
  pending: {
    titleKey: 'subscription.checkout.checking',
    bodyKey: 'subscription.checkout.delayed',
  },
  checking: { titleKey: 'subscription.checkout.checking' },
  cancelled: { titleKey: 'subscription.checkout.cancelled' },
  error: {
    titleKey: 'subscription.states.error.title',
    bodyKey: 'subscription.states.error.body',
  },
  unchanged: { titleKey: 'subscription.checkout.unchanged' },
};

/** "App Store" / "Google Play" / "Web billing" for a NEW purchase on this platform. */
export function purchaseProviderKey(platform: 'ios' | 'android' | 'web' | string) {
  if (platform === 'ios') return 'subscription.providers.appStore';
  if (platform === 'android') return 'subscription.providers.googlePlay';
  return 'subscription.providers.web';
}
